import { FormEvent, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import api, { apiError } from '../../services/api';
import { useApi } from '../../hooks/useApi';
import Select from '../../components/ui/Select';
import { Card, Field, LoadingRows, Notice, PageHeader, Toggle } from '../../components/ui/Page';
import { countryName } from '../../lib/countries';
import { CATEGORIES, TRACKS, type DocRow } from './Country';

type Form = {
  title: string;
  code: string;
  description: string;
  aiGuidance: string;
  category: string;
  tracks: DocRow['tracks'];
  required: boolean;
  isActive: boolean;
  sortOrder: string;
};

const EMPTY: Form = { title: '', code: '', description: '', aiGuidance: '', category: 'FINANCIAL', tracks: [], required: true, isActive: true, sortOrder: '' };

const toCode = (v: string) => v.toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_|_$/g, '').slice(0, 40);

export default function DocumentFormPage() {
  const { country = '', id } = useParams();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const code = country.toUpperCase();
  const editing = Boolean(id);
  const { data, loading, error: loadError } = useApi<{ doc: DocRow }>(editing ? `/admin/documents/${id}` : null);
  const [form, setForm] = useState<Form>(EMPTY);
  const [codeTouched, setCodeTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const name = data?.doc.countryName || search.get('name') || countryName(code);
  const back = `/documents/${code}`;

  useEffect(() => {
    const d = data?.doc;
    if (!d) return;
    setForm({
      title: d.title,
      code: d.code,
      description: d.description || '',
      aiGuidance: d.aiGuidance || '',
      category: d.category || '',
      tracks: d.tracks,
      required: d.required,
      isActive: d.isActive,
      sortOrder: String(d.sortOrder),
    });
    setCodeTouched(true);
  }, [data]);

  const toggleTrack = (t: DocRow['tracks'][number]) =>
    setForm((f) => ({ ...f, tracks: f.tracks.includes(t) ? f.tracks.filter((x) => x !== t) : [...f.tracks, t] }));

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    const body = {
      countryName: name,
      title: form.title,
      code: form.code,
      description: form.description || null,
      aiGuidance: form.aiGuidance || null,
      category: form.category || null,
      tracks: form.tracks,
      required: form.required,
      isActive: form.isActive,
      ...(form.sortOrder ? { sortOrder: Number(form.sortOrder) } : {}),
    };
    try {
      if (editing) await api.patch(`/admin/documents/${id}`, body);
      else await api.post('/admin/documents', { ...body, countryCode: code });
      navigate(back, { replace: true });
    } catch (err) {
      setError(apiError(err, 'Could not save the document.'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete “${form.title}” from the ${name} checklist? Uploaded files stay with their companies. To hide it temporarily, switch it off instead.`)) return;
    try {
      await api.delete(`/admin/documents/${id}`);
      navigate(back, { replace: true });
    } catch (err) {
      setError(apiError(err, 'Could not delete.'));
    }
  };

  if (editing && loading) return <LoadingRows rows={6} />;
  if (editing && !data) return <Notice tone="error">{loadError || 'Document not found.'}</Notice>;

  return (
    <div>
      <PageHeader
        crumbs={[{ label: 'Documents', to: '/documents' }, { label: name, to: back }, { label: editing ? form.title || 'Edit' : 'New document' }]}
        title={editing ? 'Edit document' : `Add document · ${name}`}
        actions={
          editing && (
            <button type="button" className="mz-btn-danger" onClick={() => void remove()}>
              Delete
            </button>
          )
        }
      />
      <form onSubmit={onSubmit} className="grid gap-4 lg:grid-cols-[1.35fr_1fr]">
        <Card title="What the customer sees">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Title" htmlFor="doc-title" className="sm:col-span-2">
              <input
                id="doc-title"
                required
                minLength={2}
                className="mz-input"
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value, code: codeTouched ? f.code : toCode(e.target.value) }))}
                placeholder="Audited financial statements (last 3 years)"
              />
            </Field>
            <Field label="Description" htmlFor="doc-desc" className="sm:col-span-2" hint="Shown to the customer under the upload slot — say exactly what to upload.">
              <textarea id="doc-desc" rows={3} className="mz-input" value={form.description} maxLength={600} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
            </Field>
            <Field label="Category" htmlFor="doc-cat">
              <Select id="doc-cat" value={form.category} onChange={(v) => setForm((f) => ({ ...f, category: v }))} options={[...CATEGORIES]} placeholder="Choose a category" />
            </Field>
            <Field label="Code" htmlFor="doc-code" hint="Stable ID used in uploads and AI remediation items.">
              <input
                id="doc-code"
                required
                minLength={2}
                className="mz-input font-mono text-[13px]"
                value={form.code}
                onChange={(e) => {
                  setCodeTouched(true);
                  setForm((f) => ({ ...f, code: toCode(e.target.value) }));
                }}
                placeholder="AUDITED_FINANCIALS"
              />
            </Field>
            <Field label="Applies to" className="sm:col-span-2" hint={form.tracks.length ? 'Only companies on the selected rating tracks are asked for it.' : 'Every company is asked for it, whatever its entity type.'}>
              <div className="flex flex-wrap gap-1.5">
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, tracks: [] }))}
                  className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${form.tracks.length === 0 ? 'bg-[#d6006e] text-white' : 'bg-white text-[#7a1f4a] hover:bg-[#fdf2f8]'}`}
                  style={form.tracks.length === 0 ? undefined : { border: '1px solid #f0cfdd' }}
                >
                  All tracks
                </button>
                {TRACKS.map((t) => {
                  const on = form.tracks.includes(t.value);
                  return (
                    <button
                      key={t.value}
                      type="button"
                      aria-pressed={on}
                      onClick={() => toggleTrack(t.value)}
                      className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${on ? 'bg-[#d6006e] text-white' : 'bg-white text-[#7a1f4a] hover:bg-[#fdf2f8]'}`}
                      style={on ? undefined : { border: '1px solid #f0cfdd' }}
                    >
                      {on ? '✓ ' : ''}
                      {t.label}
                    </button>
                  );
                })}
              </div>
            </Field>
          </div>
        </Card>

        <div className="space-y-4">
          <Card title="AI check" subtitle="Used by document verification and included in the Ratings AI prompt. Never shown to customers.">
            <textarea
              rows={7}
              className="mz-input"
              value={form.aiGuidance}
              maxLength={2000}
              onChange={(e) => setForm((f) => ({ ...f, aiGuidance: e.target.value }))}
              placeholder="Confirm three consecutive years, entity name matches, auditor signature and date. Extract revenue, EBITDA and net worth…"
            />
            <p className="mz-hint">{form.aiGuidance.length}/2000</p>
          </Card>
          <Card title="Settings">
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-[13px] font-semibold">Required</p>
                  <p className="text-xs text-[#a77d92]">Missing required documents block submission and lower the rating.</p>
                </div>
                <Toggle checked={form.required} onChange={(v) => setForm((f) => ({ ...f, required: v }))} label="Required" />
              </div>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-[13px] font-semibold">Active</p>
                  <p className="text-xs text-[#a77d92]">Inactive documents are hidden from customers and the AI.</p>
                </div>
                <Toggle checked={form.isActive} onChange={(v) => setForm((f) => ({ ...f, isActive: v }))} label="Active" />
              </div>
              <Field label="Display order" htmlFor="doc-order" hint="Lower numbers appear first. Leave blank to add at the end.">
                <input id="doc-order" type="number" min={0} className="mz-input w-32" value={form.sortOrder} onChange={(e) => setForm((f) => ({ ...f, sortOrder: e.target.value }))} />
              </Field>
            </div>
          </Card>
        </div>

        <div className="lg:col-span-2">
          {error && (
            <Notice tone="error" className="mb-2">
              {error}
            </Notice>
          )}
          <div className="mz-card sticky bottom-3 flex flex-wrap items-center justify-end gap-2 px-4 py-3">
            <button type="button" className="mz-btn-ghost" onClick={() => navigate(back)}>
              Cancel
            </button>
            <button type="submit" disabled={saving} className="mz-btn-primary">
              {saving ? 'Saving…' : editing ? 'Save document' : 'Add document'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
